import React from 'react';
import * as FW from 'fw/system/FW';

class FormattedBox extends React.Component {
	constructor(props) {
		super(props);

		let displayValue = this._format(props.value);

		if (props.startWithoutFormatting) displayValue = props.value;

		this.state = {
			value: this._parse(props.value),
			display: displayValue,
			dirty: false,
		};
	}

	// React lifecycle events
	componentDidMount() {
		if (this.props.autoFocus) this.focus();
	}

	shouldComponentUpdate(nextProps, nextState) {
		return (
			nextProps.value !== this.props.value
			|| nextProps.disabled !== this.state.disabled
			|| nextState.value !== this.state.value
			|| nextState.display !== this.state.display);
	}

	componentDidUpdate(prevProps, prevState) {
		// This wouldn't fire on user input, as the next props (coming from the form)
		// would be the same as the state value (that was updated previous on _update)
		if ((this.props.value !== prevProps.value || prevProps.shootAtUserInput) && this.props.value !== prevState.value) {
			let displayValue = this._format(this.props.value);

			if (this.props.startWithoutFormatting) displayValue = this.props.value;

			this.setState({
				value: this._parse(this.props.value),
				display: displayValue,
			});
		}
	}

	// Public API
	setValue = (value) => {
		this._update(value);
	}

	getValue = () => {
		return this.state.value;
	}

	focus = () => {
		this._input.focus();
	}

	select = () => {
		this._input.select();
	}

	flag = (status) => {
		if (!status) this._input.classList.add('invalid-field');
		else this._input.classList.remove('invalid-field');
	}

	validate = () => {
		if (this.props.onValidate) return this.props.onValidate(this.state.value);

		return null;
	}

	completed = () => {
		return !((this.state.value === '' || this.state.value === null) && this.props.required);
	}

	// Events
	onBlur = (v) => {
		this.props.onBlur && this.props.onBlur(v);
	}

	onChange = (v, d) => {
		this.props.onChange && this.props.onChange(v, d);
	}

	// Private
	_format = (value) => {
		if (this.props.formatter) return this.props.formatter(value);

		return value;
	}

	_parse = (value) => {
		if (this.props.parser) return this.props.parser(value);

		return value;
	}

	_className = () => {
		if (this.props.className !== undefined) return this.props.className;
		return '';
	}

	_filter = (e) => {
		if (this.props.filter) {
			if (!this.props.filter(e.key)) e.preventDefault();
		}
	}

	_update = (value, userTyping, callback) => {
		// value = "1234" --> The one that's on the control state (e.g. user input)
		// parsed = 1234.00 --> It's the one that goes to the form state
		// display = "1,234.00" --> what's displayed on the control
		let parsed = value;
		let display = value;

		// Prevent any formatting while typing
		if (!userTyping) {
			parsed = this._parse(value);
			display = this._format(value);
		}

		this.setState({ value, display }, () => {
			const validationResult = this.validate();
			const requiredPassed = this.completed();
			const validationPassed = (validationResult === null || validationResult === undefined);
			const status = requiredPassed && validationPassed;

			// Show error status if neccesary
			this.flag(status);

			// Prepare the value that we'll be submitting to the form
			if (this.props.valueBuilder) value = this.props.valueBuilder(display);

			// Trigger standard onChange...
			this.onChange(value, display);

			if (this.props.valueBuilder) this.props.managed && this.props.onFieldChange(value, this.props.name);
			else this.props.managed && this.props.onFieldChange(parsed, this.props.name);

			// Call callback
			callback && callback(value);
		});
	}

	_handleInputChange = (e) => {
		this.setState({
			dirty: true,
		});

		this._update(e.target.value, true);
	}

	_handleInputBlur = (e) => {
		const { value } = e.target;

		if (this.state.dirty) {
			this._update(value, false, () => {
				this.setState({
					dirty: false,
				});

				this.onBlur(value);
			});
		} else {
			this.onBlur(value);
		}
	}

	_handleInputKeyDown = () => {
		this.props.onDirt && this.props.onDirt(this);
	}

	_handleInputKeyPress = (e) => {
		this._filter(e);

		if (e.key === 'Enter') {
			e.stopPropagation();
			e.preventDefault();
			this.props.onEnter && this.props.onEnter(e.target.value);
		}
	}

	_handleInputFocus = () => {
		this.props.onFocus && this.props.onFocus();
	}

	render() {
		const opts = {
			id: this.props.id,
			name: this.props.name,
			value: this.state.display || '',
			type: this.props.type || 'text',
			maxLength: this.props.maxLength,
			placeholder: FW.getSettings().alternativeInput ? '' : this.props.placeholder,
			autoComplete: 'off',
			className: `form-control ${this._className()}`,
			disabled: this.props.disabled ? 'disabled' : undefined,
			onChange: this._handleInputChange,
			onBlur: this._handleInputBlur,
			onKeyDown: this._handleInputKeyDown,
			onFocus: this._handleInputFocus,
			onKeyPress: this.props.onKeyPress ? this.props.onKeyPress : this._handleInputKeyPress,
		};

		return (<input {...opts} ref={(input) => this._input = input} />);
	}
}

export default FormattedBox;

// This is not a form control
FormattedBox.defaultProps = {
	formControl: false,
	managed: false,
	maxLength: 30,
	shootAtUserInput: false,
};



// WEBPACK FOOTER //
// ./src/fw/forms/components/FormattedBox.js